import { useState } from 'react';
import { Home, Users, AlertCircle, FileX, Bike } from 'lucide-react';
import type { Room, Resident } from '../types';
import type { User } from '../types/auth';
import { MotorcycleOwnersModal } from './MotorcycleOwnersModal';
import { EmptyRoomsModal } from './EmptyRoomsModal';
import { LatePaymentsModal } from './LatePaymentsModal';

interface DashboardStatsProps {
  rooms: Room[];
  latePaymentsCount: number;
  residentsWithoutProof: Resident[];
  currentUser?: User | null;
  onRoomClick?: (roomNumber: string) => void;
}

export function DashboardStats({ rooms, latePaymentsCount, residentsWithoutProof, currentUser, onRoomClick }: DashboardStatsProps) {
  const [isMotorcycleModalOpen, setIsMotorcycleModalOpen] = useState(false);
  const [isEmptyRoomsModalOpen, setIsEmptyRoomsModalOpen] = useState(false);
  const [isLatePaymentsModalOpen, setIsLatePaymentsModalOpen] = useState(false);

  // If user is resident, show different stats
  if (currentUser?.role === 'resident' && currentUser?.roomNumber) {
    const userRoom = rooms.find(room => room.number === currentUser.roomNumber);
    const residentsInRoom = userRoom?.residents.length || 0;

    return (
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card p-3 sm:p-5">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-3 rounded-xl bg-primary-600 border-2 border-primary-700">
              <Home className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-semibold text-light-textSecondary dark:text-dark-textSecondary mb-0.5 sm:mb-1">Nomor Kamar</p>
              <p className="text-xl sm:text-2xl font-bold text-primary-700 dark:text-primary-400">{currentUser.roomNumber}</p>
            </div>
          </div>
        </div>

        <div className="card p-3 sm:p-5">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-3 rounded-xl bg-accent-600 border-2 border-accent-700">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-semibold text-light-textSecondary dark:text-dark-textSecondary mb-0.5 sm:mb-1">Penghuni</p>
              <p className="text-xl sm:text-2xl font-bold text-accent-700 dark:text-accent-400">{residentsInRoom}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin/Manager view (original stats)
  const occupiedRooms = rooms.filter(room => room.residents.length > 0).length;
  const totalResidents = rooms.reduce((acc, room) => acc + room.residents.length, 0);
  const emptyRooms = rooms.filter(room => room.residents.length === 0);

  // Count residents with motorcycle plates
  const residentsWithMotorcycle = rooms.reduce((acc, room) => {
    const residentsWithPlate = room.residents.filter(
      resident => resident.motorcyclePlate && resident.motorcyclePlate.trim() !== ''
    ).length;
    return acc + residentsWithPlate;
  }, 0);

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4 mb-6">
        {/* Kamar Terisi Card */}
        <div className="card p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-3 rounded-xl bg-success-600 border-2 border-success-700 flex-shrink-0">
              <Home className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-light-textSecondary dark:text-dark-textSecondary mb-0.5 sm:mb-1">Kamar Terisi</p>
              <p className="text-lg sm:text-xl font-bold text-success-700 dark:text-success-400">{occupiedRooms}/{rooms.length}</p>
            </div>
          </div>
        </div>

        {/* Kamar Kosong Card - NOW CLICKABLE */}
        <div 
          className="card p-3 sm:p-4 cursor-pointer hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
          onClick={() => setIsEmptyRoomsModalOpen(true)}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-3 rounded-xl bg-primary-600 border-2 border-primary-700 flex-shrink-0">
              <Home className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-light-textSecondary dark:text-dark-textSecondary mb-0.5 sm:mb-1">Kamar Kosong</p>
              <p className="text-lg sm:text-xl font-bold text-primary-700 dark:text-primary-400">{emptyRooms.length}</p>
              {emptyRooms.length > 0 && (
                <p className="text-[10px] sm:text-xs text-primary-600 dark:text-primary-400 mt-0.5 sm:mt-1 truncate">
                  Klik detail →
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Total Penghuni Card */}
        <div className="card p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-3 rounded-xl bg-accent-600 border-2 border-accent-700 flex-shrink-0">
              <Users className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-light-textSecondary dark:text-dark-textSecondary mb-0.5 sm:mb-1">Jumlah Penghuni</p>
              <p className="text-lg sm:text-xl font-bold text-accent-700 dark:text-accent-400">{totalResidents}</p>
            </div>
          </div>
        </div>

        {/* Tertunggak Card - NOW CLICKABLE */}
        <div 
          className="card p-3 sm:p-4 cursor-pointer hover:border-error-400 dark:hover:border-error-600 transition-colors"
          onClick={() => setIsLatePaymentsModalOpen(true)}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-3 rounded-xl bg-error-600 border-2 border-error-700 flex-shrink-0">
              <AlertCircle className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-light-textSecondary dark:text-dark-textSecondary mb-0.5 sm:mb-1">Tertunggak</p>
              <p className="text-lg sm:text-xl font-bold text-error-700 dark:text-error-400">{latePaymentsCount}</p>
              {latePaymentsCount > 0 && (
                <p className="text-[10px] sm:text-xs text-error-600 dark:text-error-400 mt-0.5 sm:mt-1 truncate">
                  Klik detail →
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Belum Upload Card */}
        <div className="card p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-3 rounded-xl bg-warning-500 border-2 border-warning-600 flex-shrink-0">
              <FileX className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-light-textSecondary dark:text-dark-textSecondary mb-0.5 sm:mb-1">Belum Upload</p>
              <p className="text-lg sm:text-xl font-bold text-warning-700 dark:text-warning-400">{residentsWithoutProof.length}</p>
              <div className="text-xs text-light-textMuted dark:text-dark-textMuted mt-1 max-h-16 overflow-y-auto">
                {residentsWithoutProof.length > 0 ? (
                  <div className="space-y-1">
                    {residentsWithoutProof.map((resident) => (
                      <div
                        key={`${resident.roomNumber}-${resident.position}`}
                        className="cursor-pointer hover:text-warning-600 dark:hover:text-warning-400 transition-colors truncate"
                        onClick={() => onRoomClick && onRoomClick(resident.roomNumber)}
                      >
                        {resident.roomNumber} ({resident.name})
                      </div>
                    ))}
                  </div>
                ) : (
                  'Semua sudah upload'
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Penghuni dengan Motor Card */}
        <div
          className="card p-3 sm:p-4 cursor-pointer hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
          onClick={() => setIsMotorcycleModalOpen(true)}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 sm:p-3 rounded-xl bg-primary-600 border-2 border-primary-700 flex-shrink-0">
              <Bike className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-light-textSecondary dark:text-dark-textSecondary mb-0.5 sm:mb-1">Ada Motor</p>
              <p className="text-lg sm:text-xl font-bold text-primary-700 dark:text-primary-400">{residentsWithMotorcycle}</p>
              <p className="text-[10px] sm:text-xs text-primary-600 dark:text-primary-400 mt-0.5 sm:mt-1">
                Klik detail →
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <MotorcycleOwnersModal
        isOpen={isMotorcycleModalOpen}
        onClose={() => setIsMotorcycleModalOpen(false)}
        rooms={rooms}
        onRoomClick={onRoomClick}
      />

      <EmptyRoomsModal
        isOpen={isEmptyRoomsModalOpen}
        onClose={() => setIsEmptyRoomsModalOpen(false)}
        emptyRooms={emptyRooms}
        onRoomClick={onRoomClick}
      />

      <LatePaymentsModal
        isOpen={isLatePaymentsModalOpen}
        onClose={() => setIsLatePaymentsModalOpen(false)}
        rooms={rooms}
        onRoomClick={onRoomClick}
      />
    </>
  );
}
